// Gravatar integration functions
// Fetch avatar and profile data from Gravatar API
import md5 from 'md5';
import { smartFetch } from '../foundation/smartfetch';
import { buildUrl } from '../foundation/urlbuilder';
/**
 * Generate MD5 hash of email for Gravatar lookups (works in browser and Node.js)
 * @param email - Email address
 * @returns MD5 hash (lowercase, trimmed)
 */
function getGravatarHash(email) {
    return md5(email.trim().toLowerCase());
}
/**
 * Get Gravatar avatar URL from email
 * @param email - Email address
 * @param size - Image size in pixels (default 200)
 * @param defaultImage - Default image type: 404, mp (mystery person), identicon, monsterid, wavatar, retro, blank
 * @returns Gravatar avatar URL
 */
export function getGravatarAvatarUrl(email, size = 200, defaultImage = 'mp') {
    const hash = getGravatarHash(email);
    return buildUrl({
        baseUrl: 'https://www.gravatar.com',
        pathSegments: ['avatar', hash],
        params: { s: size, d: defaultImage },
    });
}
/**
 * Fetch full Gravatar profile data
 * @param email - Email address
 * @returns Promise of Gravatar profile or null if not found
 */
export async function getGravatarProfile(email) {
    const hash = getGravatarHash(email);
    const url = buildUrl({
        baseUrl: 'https://en.gravatar.com',
        pathSegments: [hash + '.json'],
    });
    try {
        const data = await smartFetch(url, {});
        if (!data.entry || data.entry.length === 0)
            return null;
        return data.entry[0];
    }
    catch (error) {
        // Silently fail on CORS or network errors - Gravatar is optional
        // CORS errors are expected in some environments and don't indicate user data issues
        return null;
    }
}
/**
 * Extract social account URL by shortname
 * @param profile - Gravatar profile
 * @param shortname - Account shortname (e.g., 'github', 'linkedin', 'twitter')
 * @returns URL or undefined if not found
 */
export function getGravatarAccountUrl(profile, shortname) {
    return profile.accounts?.find((acc) => acc.shortname === shortname)?.url;
}
